import * as React from 'react';

const defaultElement = 'div';

/**
 * 'component' prop for OmakasePolymorphicRootComponent
 */
type OmakasePolymorphicRootComponentOwnProps<E extends React.ElementType = React.ElementType> = {
  component?: E;
};

/**
 * Allows passing in an HTML element or React component to guard allowable types
 * for the 'component' prop
 */
type TypeGuard<G> = {
  component?: G;
};

/**
 * Combines 'component' prop with props from the underlying element type
 */
export type OmakasePolymorphicRootComponentPropsWithRef<
  E extends React.ElementType,
  P = {},
> = OmakasePolymorphicRootComponentOwnProps<E> &
  Omit<React.ComponentPropsWithRef<E>, keyof OmakasePolymorphicRootComponentOwnProps | keyof P>;

/**
 * Root polymorphic component, to be rendered for core and primitive components
 */
export const OmakasePolymorphicRootComponent: <E extends React.ElementType = typeof defaultElement>(
  props: OmakasePolymorphicRootComponentPropsWithRef<E>
) => React.ReactElement | null = React.forwardRef(function OmakasePolymorphicRootComponent(
  props: OmakasePolymorphicRootComponentOwnProps,
  ref: React.Ref<Element>
) {
  const Element = props.component || defaultElement;
  return <Element ref={ref} {...props} component={undefined} />;
});

/**
 * Merges core / primitive component props with props from the underlying element type
 */
type PolymorphicComponentProps<
  E extends React.ElementType,
  P,
  G = {},
> = OmakasePolymorphicRootComponentPropsWithRef<E, P> & P & TypeGuard<G>;

/**
 * Props type for core / primitive component (applies type guard)
 */
export type OmakasePolymorphicComponentProps<
  P,
  E extends React.ElementType = React.ElementType,
  G = {},
> = P &
  OmakasePolymorphicRootComponentOwnProps<E> & {
    children?: React.ReactNode;
  } & TypeGuard<G>;

/**
 * Type for a ref forwarding polymorphic component (core and primitive)
 */
export type OmakasePolymorphicComponent<
  P,
  D extends React.ElementType = typeof defaultElement,
  G = {},
> = <E extends React.ElementType = D>(
  props: PolymorphicComponentProps<E, P> & TypeGuard<G>
) => React.ReactElement | null;
